import { setup, assign, enqueueActions, } from "xstate";
import { createAuthorizeActorLogic, createUploadFileActorLogic } from "./actors";
import { dlvyMCDLabels, dlvyStatLabels, } from "./utils/mapper";
// context에서 값을 안전하게 가져오는 헬퍼 함수
function getContextValue(context, key) {
    return key.split(".").reduce((acc, part) => {
        // null 또는 undefined인 경우 중단
        if (acc === null || typeof acc === "undefined") {
            return undefined;
        }
        // 현재 객체가 배열이고, part가 숫자인 경우 인덱스로 접근 시도
        if (Array.isArray(acc) && !isNaN(Number(part))) {
            return acc[Number(part)];
        }
        // 객체의 속성으로 접근
        return acc[part];
    }, context);
}
/**
 * XState 머신 Config - Exporting the internal function again for factory usage
 */
export const wowpressMachineInternal = (fileService, authService, chatService) => {
    const authorizeActor = createAuthorizeActorLogic(authService);
    const uploadFileActor = createUploadFileActorLogic(fileService);
    return setup({
        types: {
            context: {},
            events: {},
            input: {},
            output: {},
        },
        actors: {
            authorize: authorizeActor,
            uploadFile: uploadFileActor,
        },
        actions: {
            displayMedia: enqueueActions(({ context, event, enqueue }, params) => {
                console.log("[Action displayMedia enqueued] Event:", event);
                const mediaUrl = params?.url;
                if (!mediaUrl) {
                    console.warn("[Action displayMedia enqueued] No media URL provided via params.");
                    return;
                }
                if (context.ticket?.lastState && event.type === "xstate.init") {
                    console.log("[Action displayMessage enqueued] Skipping message display due to xstate.init event.");
                    return;
                }
                if (event.type === "BACK") {
                    console.log("[Action displayMessage enqueued] Skipping message display due to BACK event.");
                    return;
                }
                chatService.sendMedia(mediaUrl);
            }),
            displayMessage: enqueueActions(({ context, event, enqueue }, params) => {
                console.log("[Action displayMessage enqueued] Event:", event);
                if (context.ticket?.lastState && event.type === "xstate.init") {
                    console.log("[Action displayMessage enqueued] Skipping message display due to xstate.init event.");
                    return;
                }
                if (event.type === "BACK") {
                    console.log("[Action displayMessage enqueued] Skipping message display due to BACK event.");
                    return;
                }
                const messageFromParams = params?.message;
                const defaultMessage = "다음 안내를 확인해주세요.";
                let rawMessageToSend = messageFromParams ? messageFromParams.replace(/\\n/g, "\n") : defaultMessage;
                // {{key}} 형태의 플레이스홀더를 context 값으로 치환
                const messageToSend = rawMessageToSend.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
                    const trimmedKey = key.trim(); // 키 앞뒤 공백 제거
                    const value = getContextValue(context, trimmedKey);
                    // 값이 null, undefined인 경우 빈 문자열로 대체, 그 외에는 문자열로 변환
                    return value !== null && typeof value !== "undefined" ? String(value) : "";
                });
                chatService.send(messageToSend);
                if (!messageFromParams) {
                    console.warn("[Action displayMessage enqueued] No message provided via params, using default via UI service.");
                }
            }),
            showLoginModal: assign(({ context, event }, params) => {
                const redirect = params?.redirect;
                if (redirect) {
                    console.log("[Action showLoginModal] Redirect target received:", redirect);
                }
                return { ...context, redirect: redirect, showLoginModal: true, loginError: undefined };
            }),
            hideLoginModal: assign((args) => {
                return { ...args.context, redirect: undefined, showLoginModal: false, loginError: undefined };
            }),
            showSelectProductModal: assign({ showSelectProductModal: true }),
            hideSelectProductModal: assign({ showSelectProductModal: false }),
            showFileInput: assign({ showFileInput: true }),
            hideFileInput: assign({ showFileInput: false }),
            enableChatInput: assign({ isChatInputEnabled: true }),
            disableChatInput: assign({ isChatInputEnabled: false }),
            openLink: (_, params) => {
                const url = params?.url;
                if (url) {
                    console.log("Attempting to open link:", url);
                    try {
                        window.open(url, "_blank");
                    }
                    catch (e) {
                        console.error("Failed to open link directly:", e);
                        chatService.send(`링크를 열 수 없습니다: ${url}`);
                    }
                }
                else {
                    console.warn("openLink action called without a valid URL param.");
                }
            },
            openProductLink: enqueueActions(({ context, event, enqueue }) => {
                const productNo = context.selectedProduct?.productNo;
                const url = `https://wowpress.co.kr/ordr/prod/dets?ProdNo=${productNo}`;
                window.open(url, "_blank");
            }),
            assignFileUploadSuccess: enqueueActions(({ context, event, enqueue }) => {
                const doneEvent = event;
                console.log("[Action assignFileUploadSuccess] File uploaded:");
                chatService.send(`파일 업로드가 완료되었습니다. 계속 진행해주세요.`);
                enqueue.assign({ showFileInput: false });
            }),
            assignFileUploadFailure: enqueueActions(({ context, event, enqueue }) => {
                const errorEvent = event;
                const errorMessage = errorEvent.error instanceof Error
                    ? errorEvent.error.message
                    : String(errorEvent.error || "알 수 없는 파일 업로드 오류");
                console.error("[Action assignFileUploadFailure] Upload error:", errorMessage);
                enqueue.assign({ showFileInput: false });
            }),
            assignLoginSuccess: enqueueActions(({ context, event, enqueue }) => {
                const doneEvent = event;
                console.log("[Action assignLoginSuccess] Assigning token:", doneEvent.output?.token);
                const successMessage = doneEvent.output?.message || "로그인에 성공했습니다. 계속 진행해주세요.";
                chatService.send(successMessage);
                enqueue.assign({
                    user: { ...(context.user || {}), token: doneEvent.output?.token },
                    showLoginModal: false,
                    loginError: undefined,
                });
            }),
            assignLoginFailure: enqueueActions(({ context, event, enqueue }) => {
                const errorEvent = event;
                const errorMessage = errorEvent.error instanceof Error
                    ? errorEvent.error.message
                    : String(errorEvent.error || "알 수 없는 로그인 오류");
                console.error("[Action assignLoginFailure] Assigning error:", errorMessage);
                enqueue.assign({
                    showLoginModal: true,
                    loginError: errorMessage,
                });
            }),
            requestCustomerInfo: enqueueActions(({ context, event, enqueue }, params) => {
                console.log("[Action requestCustomerInfo] Requesting customer info:");
                enqueue.assign({ customerInfoModal: { show: true, success: params?.success, error: params?.error } });
            }),
        },
        guards: {
            checkOrderStatus: ({ context }, params) => {
                const expectedStatus = params?.condition;
                if (expectedStatus === undefined) {
                    console.warn("checkOrderStatus guard called without condition param");
                    return false;
                }
                return context.orderInfo?.raw?.ordstat?.toString() === expectedStatus?.toString();
            },
            checkShippingMethod: ({ context }, params) => {
                const expectedShippingMethodCode = params?.condition;
                if (expectedShippingMethodCode === undefined) {
                    console.warn("checkShippingMethod guard called without condition param");
                    return false;
                }
                const currentMethodName = context.orderInfo?.shippingInfo?.method;
                if (!currentMethodName)
                    return false;
                const targetMethodName = dlvyMCDLabels[expectedShippingMethodCode];
                if (!targetMethodName)
                    return false;
                return currentMethodName === targetMethodName;
            },
            checkJobStatus: ({ context }, params) => {
                const expectedJobStatus = params?.condition;
                if (expectedJobStatus === undefined) {
                    console.warn("checkJobStatus guard called without condition param or with undefined condition");
                    return false;
                }
                const currentJobStat = context.orderInfo?.raw?.jobstat;
                return (currentJobStat !== null &&
                    currentJobStat !== undefined &&
                    Number(currentJobStat) === Number(expectedJobStatus));
            },
            checkDeliveryStatus: ({ context }, params) => {
                const expectedDlvyStatus = params?.condition;
                if (expectedDlvyStatus === undefined) {
                    console.warn("checkDeliveryStatus guard called without condition param");
                    return false;
                }
                const currentDlvyStat = context.orderInfo?.shippingInfo?.status;
                return (currentDlvyStat !== null &&
                    currentDlvyStat !== undefined &&
                    currentDlvyStat === dlvyStatLabels[expectedDlvyStatus]);
            },
            checkFileErrorStatus: ({ context }, params) => {
                const expectedFileErrorStatus = params?.condition;
                if (expectedFileErrorStatus === undefined) {
                    console.warn("checkFileErrorStatus guard called without condition param");
                    return false;
                }
                return context.orderInfo?.fileInfo?.filestat === expectedFileErrorStatus;
            },
            checkAuth: ({ context }, params) => {
                const expectedAuth = typeof params?.condition === "string"
                    ? params.condition.toLowerCase() === "true"
                    : Boolean(params?.condition ?? false);
                return !!context.user?.token === expectedAuth;
            },
            hasOrder: ({ context }, params) => {
                const expectedOrder = typeof params?.condition === "string"
                    ? params.condition.toLowerCase() === "true"
                    : Boolean(params?.condition ?? false);
                return !!context.orderCount === expectedOrder;
            },
            hasFileError: ({ context }) => {
                const currentJobStat = context.orderInfo?.fileInfo?.filestat;
                if (currentJobStat === null || currentJobStat === undefined) {
                    console.warn("hasFileError guard called when jobstat is null or undefined");
                    return false;
                }
                return currentJobStat !== null;
            },
        },
    });
};
export const internalMachineEvents = {
    "xstate.USER_AUTH_INFO": {
        actions: assign({
            user: (_) => _.event.context.user || {},
        }),
    },
    "xstate.UPDATE_CONTEXT": {
        actions: assign((assignArgs) => {
            const contextUpdates = {};
            console.log("assignArgs.event.context", assignArgs.event.context);
            if ("user" in assignArgs.event.context)
                contextUpdates.user = assignArgs.event.context.user;
            if ("orderInfo" in assignArgs.event.context)
                contextUpdates.orderInfo = assignArgs.event.context.orderInfo;
            if ("ticket" in assignArgs.event.context)
                contextUpdates.ticket = assignArgs.event.context.ticket;
            if ("isChatInputEnabled" in assignArgs.event.context)
                contextUpdates.isChatInputEnabled = assignArgs.event.context.isChatInputEnabled;
            return contextUpdates;
        }),
    },
    "xstate.LOGIN_SUCCESS": {
        actions: [{ type: "assignLoginSuccess" }],
    },
    "xstate.LOGIN_FAILURE": {
        target: "..",
        actions: [{ type: "assignLoginFailure" }],
    },
    "xstate.AUTH_CANCEL": {
        actions: [{ type: "displayMessage", params: { message: "로그인이 취소되었습니다. 로그인 후 이용해 주세요." } }],
    },
    "xstate.FILE_UPLOAD_START": {
        actions: [
            {
                type: "displayMessage",
                params: {
                    message: "파일 업로드 중입니다. 잠시만 기다려주세요.",
                },
            },
        ],
    },
    "xstate.FILE_UPLOAD_SUCCESS": {
        actions: [{ type: "assignFileUploadSuccess" }],
    },
    "xstate.FILE_UPLOAD_FAILURE": {
        actions: [{ type: "assignFileUploadFailure" }],
    },
    "xstate.PRODUCT_SELECTION_SUCCESS": {
        target: ".pre_order_product_selection",
        actions: assign((assignArgs) => {
            const selectedProduct = assignArgs.event.selectedProduct;
            return { selectedProduct, showSelectProductModal: false };
        }),
    },
    "xstate.PRODUCT_SELECTION_FAILURE": {
        actions: [
            {
                type: "displayMessage",
                params: {
                    message: "상품 선택 후 다시 시도해 주세요.",
                },
            },
            {
                type: "hideSelectProductModal",
            },
        ],
    },
    "xstate.USER_INPUT": {
        actions: assign({
            user: (_) => _.event.context.user || {},
        }),
    },
    "xstate.END_CHATBOT": {
        target: ".additional_inquiry",
    },
};
